import * as React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpenText, ChevronDown, ChevronRight, Search, Wallet } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/components/pagination";
import { AdvertiserSelect } from "@/components/advertiser-select";
import { AffiliateSelect } from "@/components/affiliate-select";
import { AdvertiserCell, AffiliateCell, useAdvertiserLookup, useAffiliateLookup } from "@/components/advertiser-cell";
import { getMarginRows, listAccruals, type FinanceFilter } from "@/api/cpa";
import { listCampaignsPaged } from "@/api/campaigns";
import { cn, formatDate } from "@/lib/utils";
import { formatUsd } from "@/lib/money";
import type { DealAccrualDto } from "@/types/cpa";

const LEDGER_PAGE_SIZE = 50;

function currentMonth(): string {
    return new Date().toISOString().slice(0, 7);
}

export function FinancePage(): React.ReactNode {
    const [month, setMonth] = React.useState(currentMonth());
    const [advertiserId, setAdvertiserId] = React.useState<number | null | undefined>(undefined);
    const [affiliateId, setAffiliateId] = React.useState<number | null | undefined>(undefined);
    const [campaignId, setCampaignId] = React.useState<number | "all">("all");
    const [ledgerOpen, setLedgerOpen] = React.useState(false);
    const [ledgerPage, setLedgerPage] = React.useState(0);

    const advLookup = useAdvertiserLookup();
    const affLookup = useAffiliateLookup();
    const campaignsQuery = useQuery({
        queryKey: ["campaigns", "deal-scope"],
        queryFn: () => listCampaignsPaged({ size: 200, sort: "name,asc" }),
        staleTime: 60_000,
    });
    const campaigns = campaignsQuery.data?.content ?? [];
    const campaignName = (id: number | null): string =>
        id == null ? "—" : campaigns.find((c) => c.id === id)?.name ?? `#${id}`;

    const filter: FinanceFilter = {
        advertiserId: typeof advertiserId === "number" ? advertiserId : undefined,
        affiliateId: typeof affiliateId === "number" ? affiliateId : undefined,
        campaignId: campaignId === "all" ? undefined : campaignId,
    };
    const monthValid = /^\d{4}-\d{2}$/.test(month);

    const marginQuery = useQuery({
        queryKey: ["finance", "margin", month, filter],
        queryFn: () => getMarginRows(month, filter),
        enabled: monthValid,
    });

    React.useEffect(() => {
        setLedgerPage(0);
    }, [advertiserId, affiliateId, campaignId]);

    const accrualsQuery = useQuery({
        queryKey: ["finance", "accruals", filter, ledgerPage],
        queryFn: () => listAccruals(filter, ledgerPage, LEDGER_PAGE_SIZE),
        enabled: ledgerOpen,
    });

    const rows = marginQuery.data ?? [];
    const totals = rows.reduce(
        (acc, r) => ({
            revenue: acc.revenue + r.revenueUsdMicros,
            payout: acc.payout + r.payoutUsdMicros,
            margin: acc.margin + r.marginUsdMicros,
        }),
        { revenue: 0, payout: 0, margin: 0 },
    );
    const marginPct = totals.revenue > 0 ? (totals.margin / totals.revenue) * 100 : null;

    return (
        <>
            <PageHeader
                title="Finance"
                description="Monthly revenue, payouts and margin from the pricing ledger."
            />
            <div className="space-y-4 p-6">
                {/* Filters */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <Search className="h-4 w-4" /> Filters
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="finance-month">Month</Label>
                                <Input
                                    id="finance-month"
                                    type="month"
                                    value={month}
                                    onChange={(e) => setMonth(e.target.value)}
                                    data-testid="finance-month-input"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Advertiser</Label>
                                <AdvertiserSelect
                                    value={advertiserId}
                                    onChange={setAdvertiserId}
                                    includeUnassigned={false}
                                    data-testid="finance-advertiser-filter"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Affiliate</Label>
                                <AffiliateSelect
                                    value={affiliateId}
                                    onChange={setAffiliateId}
                                    includeUnassigned={false}
                                    data-testid="finance-affiliate-filter"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Campaign</Label>
                                <Select
                                    value={campaignId === "all" ? "all" : campaignId.toString()}
                                    onValueChange={(v) => setCampaignId(v === "all" ? "all" : Number(v))}
                                >
                                    <SelectTrigger data-testid="finance-campaign-filter">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All campaigns</SelectItem>
                                        {campaigns.map((c) => (
                                            <SelectItem key={c.id} value={c.id.toString()}>
                                                {c.name} · {c.domainHostname}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Summary strip */}
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <SummaryCell label="Revenue" value={formatUsd(totals.revenue)} testid="finance-kpi-revenue" />
                    <SummaryCell label="Payout" value={formatUsd(totals.payout)} testid="finance-kpi-payout" />
                    <SummaryCell
                        label="Margin"
                        value={formatUsd(totals.margin)}
                        accent
                        negative={totals.margin < 0}
                        testid="finance-kpi-margin"
                    />
                    <SummaryCell
                        label="Margin %"
                        value={marginPct == null ? "—" : `${marginPct.toFixed(1)}%`}
                        negative={marginPct != null && marginPct < 0}
                        testid="finance-kpi-margin-pct"
                    />
                </div>

                {/* Margin table */}
                {!monthValid ? (
                    <EmptyState title="Pick a month" description="Choose a month above to see its revenue and payouts." />
                ) : marginQuery.isLoading ? (
                    <div className="flex justify-center py-12">
                        <Spinner size="lg" />
                    </div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        icon={<Wallet className="h-8 w-8" />}
                        title="Nothing priced this month"
                        description="Rows appear here once traffic gets priced by an active deal."
                    />
                ) : (
                    <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full text-sm" data-testid="finance-margin-table">
                            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left">Advertiser</th>
                                    <th className="px-3 py-2 text-left">Affiliate</th>
                                    <th className="px-3 py-2 text-left">Campaign</th>
                                    <th className="px-3 py-2 text-left">Action</th>
                                    <th className="px-3 py-2 text-right">Revenue</th>
                                    <th className="px-3 py-2 text-right">Payout</th>
                                    <th className="px-3 py-2 text-right">Margin</th>
                                    <th className="px-3 py-2 text-right">Paid in / out</th>
                                    <th className="px-3 py-2 text-right">Capped</th>
                                    <th className="px-3 py-2 text-right">Held (test)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((r, i) => (
                                    <tr key={i} className="hover:bg-muted/30">
                                        <td className="px-3 py-2">
                                            <AdvertiserCell advertiserId={r.advertiserId} lookup={advLookup.byId} />
                                        </td>
                                        <td className="px-3 py-2">
                                            <AffiliateCell affiliateId={r.affiliateId} lookup={affLookup.byId} />
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground">
                                            {r.campaignId != null ? (
                                                <Link to={`/campaigns/${r.campaignId}`} className="hover:underline">
                                                    {campaignName(r.campaignId)}
                                                </Link>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <Badge variant="outline">{r.actionType}</Badge>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">{formatUsd(r.revenueUsdMicros)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{formatUsd(r.payoutUsdMicros)}</td>
                                        <td
                                            className={cn(
                                                "px-3 py-2 text-right font-medium tabular-nums",
                                                r.marginUsdMicros < 0 && "text-rose-600 dark:text-rose-400",
                                            )}
                                        >
                                            {formatUsd(r.marginUsdMicros)}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                            {r.revenueActions.toLocaleString("en-US")} / {r.payoutActions.toLocaleString("en-US")}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                            {r.cappedCount > 0 ? r.cappedCount.toLocaleString("en-US") : "—"}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                            {r.testGatedCount > 0 ? r.testGatedCount.toLocaleString("en-US") : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Ledger drill-down */}
                <div className="rounded-md border border-border bg-card">
                    <button
                        type="button"
                        onClick={() => setLedgerOpen((v) => !v)}
                        className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium hover:bg-accent/40"
                        data-testid="finance-ledger-toggle"
                    >
                        {ledgerOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <BookOpenText className="h-4 w-4" /> Ledger
                        <span className="text-xs font-normal text-muted-foreground">
                            — every priced action, newest first (uses the filters above; not limited to the month)
                        </span>
                    </button>
                    {ledgerOpen && (
                        <div className="border-t border-border p-4">
                            {accrualsQuery.isLoading ? (
                                <div className="flex justify-center py-8">
                                    <Spinner />
                                </div>
                            ) : accrualsQuery.data && accrualsQuery.data.content.length > 0 ? (
                                <>
                                    <div className="overflow-x-auto rounded-md border border-border">
                                        <table className="w-full text-sm" data-testid="finance-ledger-table">
                                            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                                <tr>
                                                    <th className="px-3 py-2 text-left">When</th>
                                                    <th className="px-3 py-2 text-left">Direction</th>
                                                    <th className="px-3 py-2 text-left">Action</th>
                                                    <th className="px-3 py-2 text-right">Amount</th>
                                                    <th className="px-3 py-2 text-left">Flags</th>
                                                    <th className="px-3 py-2 text-left">Period</th>
                                                    <th className="px-3 py-2 text-left">Lead</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border">
                                                {accrualsQuery.data.content.map((a) => (
                                                    <LedgerRow key={a.id} a={a} />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Pagination
                                        page={accrualsQuery.data}
                                        pageSize={LEDGER_PAGE_SIZE}
                                        currentPage={ledgerPage}
                                        onPage={setLedgerPage}
                                    />
                                </>
                            ) : (
                                <p className="py-4 text-center text-sm text-muted-foreground">
                                    No ledger entries match these filters yet.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

function SummaryCell({
    label,
    value,
    accent = false,
    negative = false,
    testid,
}: {
    label: string;
    value: string;
    accent?: boolean;
    negative?: boolean;
    testid: string;
}): React.ReactNode {
    return (
        <div
            className={cn(
                "rounded-md border border-border bg-card p-3",
                accent && "border-primary/30 bg-primary/5",
            )}
            data-testid={testid}
        >
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div
                className={cn(
                    "mt-1 text-xl font-semibold tabular-nums",
                    negative && "text-rose-600 dark:text-rose-400",
                )}
            >
                {value}
            </div>
        </div>
    );
}

function LedgerRow({ a }: { a: DealAccrualDto }): React.ReactNode {
    return (
        <tr className="hover:bg-muted/30">
            <td className="px-3 py-2 text-muted-foreground">{formatDate(a.accruedAt)}</td>
            <td className="px-3 py-2">
                <Badge variant={a.side === "advertiser" ? "success" : "secondary"}>
                    {a.side === "advertiser" ? "money in" : "money out"}
                </Badge>
            </td>
            <td className="px-3 py-2">
                <Badge variant="outline">{a.actionType}</Badge>
            </td>
            <td className="px-3 py-2 text-right tabular-nums">{formatUsd(a.amountUsdMicros)}</td>
            <td className="px-3 py-2">
                <span className="flex flex-wrap gap-1">
                    {a.capped === true && <Badge variant="warning">capped</Badge>}
                    {a.testGated === true && <Badge variant="muted">held (test)</Badge>}
                    {a.capped !== true && a.testGated !== true && (
                        <span className="text-muted-foreground">—</span>
                    )}
                </span>
            </td>
            <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{a.periodKey ?? "—"}</td>
            <td className="px-3 py-2">
                {a.leadId != null ? (
                    <Link to={`/leads?id=${a.leadId}`} className="hover:underline">
                        #{a.leadId}
                    </Link>
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </td>
        </tr>
    );
}
